/**
 * Compact relative-time helper ("just now", "5m", "2h", "3d", "2w").
 * Used by the prayer wall (candle footer, encouragement timestamps) —
 * the web app has no date-fns dependency, so this stands in for
 * formatDistanceToNow on mobile.
 *
 * Total function: accepts ISO strings, Date, Firestore Timestamp objects
 * (client `{ toDate() }` or JSON-serialized `{ _seconds }`), or garbage,
 * and returns '' for anything unparseable. It must NEVER throw — a single
 * malformed timestamp in one prayer document would otherwise crash the
 * whole page at render time.
 */
type TimestampLike =
  | string
  | Date
  | { toDate: () => Date }
  | { _seconds: number }
  | { seconds: number }
  | null
  | undefined;

export function timestampMillis(value: TimestampLike): number {
  try {
    if (value == null) return NaN;
    if (typeof value === 'string') return new Date(value).getTime();
    if (value instanceof Date) return value.getTime();
    if (typeof (value as any).toDate === 'function') return (value as any).toDate().getTime();
    if (typeof (value as any)._seconds === 'number') return (value as any)._seconds * 1000;
    if (typeof (value as any).seconds === 'number') return (value as any).seconds * 1000;
    return new Date(value as any).getTime();
  } catch {
    return NaN;
  }
}

export function timeAgo(value: TimestampLike): string {
  const time = timestampMillis(value);
  if (!Number.isFinite(time)) return '';

  const seconds = Math.max(0, Math.floor((Date.now() - time) / 1000));
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;

  return `${Math.floor(days / 365)}y`;
}
